import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { colaboradoresAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ColaboradorFormDialog } from './colaboradores/ColaboradorFormDialog'
import {
  Plus,
  Search,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UserX,
  Mail,
  Phone,
  Briefcase,
} from 'lucide-react'

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  CLT: 'CLT',
  PJ: 'PJ',
  ESTAGIO: 'Estágio',
  TEMPORARIO: 'Temporário',
  AUTONOMO: 'Autônomo',
}

const CONTRACT_TYPE_COLORS: Record<string, string> = {
  CLT: 'bg-blue-100 text-blue-700',
  PJ: 'bg-purple-100 text-purple-700',
  ESTAGIO: 'bg-green-100 text-green-700',
  TEMPORARIO: 'bg-amber-100 text-amber-700',
  AUTONOMO: 'bg-neutral-100 text-neutral-700',
}

export function Colaboradores() {
  const navigate = useNavigate()
  const canCreate = usePermission('colaboradores:create')

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['colaboradores', { page, search, contractType: contractTypeFilter, isActive: statusFilter }],
    queryFn: () =>
      colaboradoresAPI.list({
        page,
        limit: 9,
        search: search || undefined,
        contractType: contractTypeFilter || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'true',
      }),
  })

  const colaboradores = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Colaboradores</h1>
          <p className="mt-1 text-neutral-600">Gerencie os colaboradores da empresa</p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Colaborador
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por nome, CPF ou cargo..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <Select
          value={contractTypeFilter || 'ALL'}
          onValueChange={(value) => {
            setContractTypeFilter(value === 'ALL' ? '' : value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tipo de contrato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os tipos</SelectItem>
            {Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={statusFilter || 'ALL'}
          onValueChange={(value) => {
            setStatusFilter(value === 'ALL' ? '' : value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="true">Ativo</SelectItem>
            <SelectItem value="false">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : colaboradores.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <UserX className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || contractTypeFilter || statusFilter
              ? 'Nenhum colaborador encontrado'
              : 'Nenhum colaborador cadastrado'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || contractTypeFilter || statusFilter
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece cadastrando o primeiro colaborador da empresa.'}
          </p>
          {canCreate && !search && !contractTypeFilter && !statusFilter && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Cadastrar Primeiro Colaborador
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {colaboradores.map((colaborador: any) => (
              <Card
                key={colaborador.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/colaboradores/${colaborador.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Users className="h-5 w-5 shrink-0 text-primary" />
                      <CardTitle className="text-base line-clamp-1">
                        {colaborador.name}
                      </CardTitle>
                    </div>
                    <Badge variant={colaborador.isActive ? 'default' : 'secondary'} className="shrink-0 ml-2">
                      {colaborador.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-neutral-500">{formatDocument(colaborador.document)}</p>

                  <div className="flex items-center gap-2">
                    <Briefcase className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                    <span className="text-sm text-neutral-700 truncate">{colaborador.cargo}</span>
                    {colaborador.department && (
                      <span className="text-xs text-neutral-400 truncate">· {colaborador.department}</span>
                    )}
                  </div>

                  <Badge
                    variant="outline"
                    className={CONTRACT_TYPE_COLORS[colaborador.contractType] || ''}
                  >
                    {CONTRACT_TYPE_LABELS[colaborador.contractType] || colaborador.contractType}
                  </Badge>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-500">
                    {colaborador.email && (
                      <div className="flex items-center gap-1 min-w-0">
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{colaborador.email}</span>
                      </div>
                    )}
                    {colaborador.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{colaborador.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Mostrando {colaboradores.length} de {pagination.total} colaboradores
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Próximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ColaboradorFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  )
}
